import { Link } from 'react-router';
import { ThemeToggle } from '@mieweb/ui';
import { BrandLockup } from '../components/BrandMark';

export function PrivacyPage() {
  const lastUpdated = 'February 4, 2026';
  const effectiveDate = 'February 4, 2026';

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 dark:from-neutral-950 to-white dark:to-neutral-900">
      {/* Navigation */}
      <nav className="bg-white dark:bg-neutral-900 border-b border-slate-200 dark:border-neutral-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <BrandLockup
                iconClassName="h-7 w-7"
                textClassName="text-xl font-bold text-slate-900 dark:text-white"
              />
            </Link>
            <div className="flex items-center gap-3">
              <ThemeToggle mode="two-way" size="sm" variant="ghost" />
              <Link to="/login" className="text-slate-600 dark:text-neutral-400 hover:text-slate-900 dark:hover:text-white transition-colors">
                Sign In
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="prose prose-slate dark:prose-invert max-w-none">
          <h1 className="text-4xl font-bold text-slate-900 dark:text-white mb-2">Privacy Policy</h1>
          <p className="text-slate-500 dark:text-neutral-400 mb-8">Last updated: {lastUpdated} | Effective: {effectiveDate}</p>

          <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-700/40 rounded-lg p-4 mb-8">
            <p className="text-amber-800 dark:text-amber-300 text-sm">
              <strong>Summary:</strong> We collect minimal data needed to provide the Service. We don't 
              sell your data. We use industry-standard security practices. You control your data and 
              can request deletion at any time.
            </p>
          </div>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-slate-900 dark:text-white mb-4">1. Introduction</h2>
            <p className="text-slate-600 dark:text-neutral-400 mb-4">
              BlueHive Health, LLC ("BlueHive Health," "we," "us," or "our") is committed to protecting 
              your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard 
              your information when you use the Pollenate feedback platform and related services 
              (collectively, the "Service").
            </p>
            <p className="text-slate-600 dark:text-neutral-400">
              Please read this Privacy Policy carefully. By using the Service, you consent to the 
              collection and use of your information as described in this Privacy Policy. If you do 
              not agree with our policies and practices, please do not use the Service.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-slate-900 dark:text-white mb-4">2. Information We Collect</h2>
            
            <h3 className="text-xl font-medium text-slate-800 dark:text-neutral-200 mb-3">2.1 Information You Provide</h3>
            <p className="text-slate-600 dark:text-neutral-400 mb-4">We collect information you voluntarily provide, including:</p>
            <ul className="list-disc list-inside text-slate-600 dark:text-neutral-400 mb-4 space-y-2">
              <li><strong>Account Information:</strong> Email address, display name, and organization name when you create an account</li>
              <li><strong>Profile Information:</strong> Optional profile details you choose to add</li>
              <li><strong>Payment Information:</strong> Billing details processed securely by our payment processor (we do not store credit card numbers)</li>
              <li><strong>Communications:</strong> Information you provide when contacting our support team</li>
              <li><strong>Feedback Configuration:</strong> Settings for your inboxes, widgets, and integrations</li>
            </ul>

            <h3 className="text-xl font-medium text-slate-800 dark:text-neutral-200 mb-3">2.2 Information Collected Automatically</h3>
            <p className="text-slate-600 dark:text-neutral-400 mb-4">When you use the Service, we automatically collect:</p>
            <ul className="list-disc list-inside text-slate-600 dark:text-neutral-400 mb-4 space-y-2">
              <li><strong>Usage Data:</strong> Pages visited, features used, and actions taken within the Service</li>
              <li><strong>Device Information:</strong> Browser type, operating system, device type, and IP address</li>
              <li><strong>Log Data:</strong> Access times, error logs, and referring URLs</li>
              <li><strong>Cookies:</strong> Session identifiers and preferences (see Section 7)</li>
            </ul>

            <h3 className="text-xl font-medium text-slate-800 dark:text-neutral-200 mb-3">2.3 Feedback Data</h3>
            <p className="text-slate-600 dark:text-neutral-400 mb-4">
              As a feedback platform, we process data submitted by your end users through the widgets 
              you deploy. This may include:
            </p>
            <ul className="list-disc list-inside text-slate-600 dark:text-neutral-400 mb-4 space-y-2">
              <li>Ratings and scores (thumbs, stars, NPS, CSAT, emoji)</li>
              <li>Text comments and feedback</li>
              <li>Page URLs where feedback was submitted</li>
              <li>Timestamp of submission</li>
              <li>Any custom context data you configure to collect</li>
            </ul>
            <p className="text-slate-600 dark:text-neutral-400">
              <strong>Important:</strong> You are the data controller for feedback collected from your 
              users. You are responsible for providing appropriate privacy notices to your users and 
              obtaining any necessary consents.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-slate-900 dark:text-white mb-4">3. How We Use Your Information</h2>
            <p className="text-slate-600 dark:text-neutral-400 mb-4">We use the information we collect to:</p>
            <ul className="list-disc list-inside text-slate-600 dark:text-neutral-400 mb-4 space-y-2">
              <li>Provide, maintain, and improve the Service</li>
              <li>Process transactions and send related information</li>
              <li>Send administrative messages, updates, and security alerts</li>
              <li>Respond to your comments, questions, and support requests</li>
              <li>Analyze usage patterns to improve user experience</li>
              <li>Detect, prevent, and address technical issues and security threats</li>
              <li>Comply with legal obligations</li>
              <li>Enforce our Terms of Service</li>
            </ul>
            <p className="text-slate-600 dark:text-neutral-400">
              We do not use feedback data collected through your widgets for any purpose other than 
              providing the Service to you.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-slate-900 dark:text-white mb-4">4. Information Sharing and Disclosure</h2>
            <p className="text-slate-600 dark:text-neutral-400 mb-4">
              <strong>We do not sell your personal information.</strong> We may share your information 
              only in the following circumstances:
            </p>

            <h3 className="text-xl font-medium text-slate-800 dark:text-neutral-200 mb-3">4.1 Service Providers</h3>
            <p className="text-slate-600 dark:text-neutral-400 mb-4">
              We share information with third-party service providers who perform services on our behalf:
            </p>
            <ul className="list-disc list-inside text-slate-600 dark:text-neutral-400 mb-4 space-y-2">
              <li><strong>Cloudflare:</strong> Hosting, CDN, and security services</li>
              <li><strong>Mailgun:</strong> Email delivery for authentication codes</li>
              <li><strong>OpenAI:</strong> AI-powered semantic search features (when enabled)</li>
              <li><strong>Payment Processors:</strong> Secure payment processing</li>
            </ul>
            <p className="text-slate-600 dark:text-neutral-400 mb-4">
              These providers are bound by contractual obligations to keep your information confidential 
              and use it only for the purposes for which we disclose it to them.
            </p>

            <h3 className="text-xl font-medium text-slate-800 dark:text-neutral-200 mb-3">4.2 Legal Requirements</h3>
            <p className="text-slate-600 dark:text-neutral-400 mb-4">
              We may disclose your information if required to do so by law or in response to valid 
              requests by public authorities (e.g., court order, subpoena, government request).
            </p>

            <h3 className="text-xl font-medium text-slate-800 dark:text-neutral-200 mb-3">4.3 Business Transfers</h3>
            <p className="text-slate-600 dark:text-neutral-400 mb-4">
              If BlueHive Health is involved in a merger, acquisition, or sale of assets, your 
              information may be transferred as part of that transaction. We will notify you of 
              any change in ownership or uses of your information.
            </p>

            <h3 className="text-xl font-medium text-slate-800 dark:text-neutral-200 mb-3">4.4 With Your Consent</h3>
            <p className="text-slate-600 dark:text-neutral-400">
              We may share your information with third parties when you give us explicit consent to do so.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-slate-900 dark:text-white mb-4">5. Data Security</h2>
            <p className="text-slate-600 dark:text-neutral-400 mb-4">
              We implement appropriate technical and organizational measures to protect your information:
            </p>
            <ul className="list-disc list-inside text-slate-600 dark:text-neutral-400 mb-4 space-y-2">
              <li><strong>Encryption:</strong> All data is encrypted in transit (TLS 1.3) and at rest</li>
              <li><strong>Access Controls:</strong> Strict access controls and authentication requirements</li>
              <li><strong>Infrastructure:</strong> Hosted on Cloudflare's secure global network</li>
              <li><strong>Monitoring:</strong> Continuous security monitoring and logging</li>
              <li><strong>Backups:</strong> Regular encrypted backups with secure storage</li>
            </ul>
            <p className="text-slate-600 dark:text-neutral-400">
              While we strive to protect your information, no method of transmission over the Internet 
              or electronic storage is 100% secure. We cannot guarantee absolute security.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-slate-900 dark:text-white mb-4">6. Data Retention</h2>
            <p className="text-slate-600 dark:text-neutral-400 mb-4">
              We retain your information for as long as necessary to provide the Service and fulfill 
              the purposes described in this Privacy Policy:
            </p>
            <ul className="list-disc list-inside text-slate-600 dark:text-neutral-400 mb-4 space-y-2">
              <li><strong>Account Data:</strong> Retained while your account is active</li>
              <li><strong>Feedback Data:</strong> Retained according to your plan's data retention settings</li>
              <li><strong>Usage Logs:</strong> Retained for up to 90 days for security and analytics</li>
              <li><strong>Backup Data:</strong> Retained for up to 30 days after deletion</li>
            </ul>
            <p className="text-slate-600 dark:text-neutral-400">
              Upon account deletion, we will delete or anonymize your data within 30 days, except 
              where we are required by law to retain certain information.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-slate-900 dark:text-white mb-4">7. Cookies and Tracking</h2>
            <p className="text-slate-600 dark:text-neutral-400 mb-4">We use the following types of cookies:</p>
            <ul className="list-disc list-inside text-slate-600 dark:text-neutral-400 mb-4 space-y-2">
              <li><strong>Essential Cookies:</strong> Required for the Service to function (authentication, security)</li>
              <li><strong>Preference Cookies:</strong> Remember your settings and preferences</li>
              <li><strong>Analytics Cookies:</strong> Help us understand how you use the Service (optional)</li>
            </ul>
            <p className="text-slate-600 dark:text-neutral-400">
              You can control cookies through your browser settings. Note that disabling essential 
              cookies may prevent you from using certain features of the Service.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-slate-900 dark:text-white mb-4">8. Your Rights and Choices</h2>
            <p className="text-slate-600 dark:text-neutral-400 mb-4">Depending on your location, you may have the following rights:</p>

            <div className="bg-slate-100 dark:bg-neutral-800 rounded-lg p-6 mb-4">
              <h3 className="text-lg font-medium text-slate-800 dark:text-neutral-200 mb-3">All Users</h3>
              <ul className="list-disc list-inside text-slate-600 dark:text-neutral-400 space-y-2">
                <li><strong>Access:</strong> Request a copy of your personal information</li>
                <li><strong>Correction:</strong> Request correction of inaccurate information</li>
                <li><strong>Deletion:</strong> Request deletion of your account and data</li>
                <li><strong>Export:</strong> Export your data in a portable format</li>
                <li><strong>Opt-out:</strong> Opt out of marketing communications</li>
              </ul>
            </div>

            <div className="bg-slate-100 dark:bg-neutral-800 rounded-lg p-6 mb-4">
              <h3 className="text-lg font-medium text-slate-800 dark:text-neutral-200 mb-3">European Users (GDPR)</h3>
              <ul className="list-disc list-inside text-slate-600 dark:text-neutral-400 space-y-2">
                <li><strong>Legal Basis:</strong> We process your data based on consent, contract performance, or legitimate interests</li>
                <li><strong>Restriction:</strong> Request restriction of processing</li>
                <li><strong>Objection:</strong> Object to processing based on legitimate interests</li>
                <li><strong>Portability:</strong> Receive your data in a structured, machine-readable format</li>
                <li><strong>Complaint:</strong> Lodge a complaint with your local data protection authority</li>
              </ul>
            </div>

            <div className="bg-slate-100 dark:bg-neutral-800 rounded-lg p-6">
              <h3 className="text-lg font-medium text-slate-800 dark:text-neutral-200 mb-3">California Users (CCPA)</h3>
              <ul className="list-disc list-inside text-slate-600 dark:text-neutral-400 space-y-2">
                <li><strong>Know:</strong> Right to know what personal information is collected</li>
                <li><strong>Delete:</strong> Right to request deletion of personal information</li>
                <li><strong>Non-Discrimination:</strong> Right to non-discrimination for exercising your rights</li>
                <li><strong>Opt-Out:</strong> Right to opt out of the sale of personal information (we don't sell data)</li>
              </ul>
            </div>

            <p className="text-slate-600 dark:text-neutral-400 mt-4">
              To exercise these rights, please contact us at privacy@pollenate.dev. We will respond 
              to your request within 30 days.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-slate-900 dark:text-white mb-4">9. International Data Transfers</h2>
            <p className="text-slate-600 dark:text-neutral-400 mb-4">
              Your information may be transferred to and processed in countries other than your own. 
              Our primary infrastructure is located in the United States and Cloudflare's global network.
            </p>
            <p className="text-slate-600 dark:text-neutral-400">
              When we transfer data from the European Economic Area (EEA) or United Kingdom, we rely 
              on Standard Contractual Clauses approved by the European Commission and other appropriate 
              safeguards to ensure adequate protection of your data.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-slate-900 dark:text-white mb-4">10. Healthcare Data (HIPAA)</h2>
            <p className="text-slate-600 dark:text-neutral-400 mb-4">
              If you are a covered entity or business associate under HIPAA and wish to use Pollenate 
              to collect Protected Health Information (PHI), please contact us to execute a Business 
              Associate Agreement (BAA) before using the Service for such purposes.
            </p>
            <p className="text-slate-600 dark:text-neutral-400">
              Enterprise plans include HIPAA-compliant features such as enhanced encryption, audit 
              logging, and BAA availability.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-slate-900 dark:text-white mb-4">11. Children's Privacy</h2>
            <p className="text-slate-600 dark:text-neutral-400">
              The Service is not intended for individuals under the age of 16. We do not knowingly 
              collect personal information from children under 16. If we become aware that a child 
              under 16 has provided us with personal information, we will take steps to delete such 
              information promptly.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-slate-900 dark:text-white mb-4">12. Changes to This Privacy Policy</h2>
            <p className="text-slate-600 dark:text-neutral-400 mb-4">
              We may update this Privacy Policy from time to time. We will notify you of any material 
              changes by:
            </p>
            <ul className="list-disc list-inside text-slate-600 dark:text-neutral-400 mb-4 space-y-2">
              <li>Posting the updated Privacy Policy on the Service</li>
              <li>Updating the "Last updated" date at the top of this page</li>
              <li>Sending you an email notification (for material changes)</li>
            </ul>
            <p className="text-slate-600 dark:text-neutral-400">
              We encourage you to review this Privacy Policy periodically to stay informed about how 
              we are protecting your information.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-slate-900 dark:text-white mb-4">13. Contact Us</h2>
            <p className="text-slate-600 dark:text-neutral-400 mb-4">
              If you have any questions about this Privacy Policy or our privacy practices, please 
              contact us:
            </p>
            <div className="bg-slate-100 dark:bg-neutral-800 rounded-lg p-6">
              <p className="text-slate-700 dark:text-neutral-300 font-medium">BlueHive Health, LLC</p>
              <p className="text-slate-600 dark:text-neutral-400">Privacy Team</p>
              <p className="text-slate-600 dark:text-neutral-400">Email: privacy@pollenate.dev</p>
              <p className="text-slate-600 dark:text-neutral-400">Website: <a href="https://pollenate.dev" className="text-primary hover:underline">pollenate.dev</a></p>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-slate-900 dark:text-white mb-4">14. Data Protection Officer</h2>
            <p className="text-slate-600 dark:text-neutral-400">
              For inquiries related to GDPR compliance, you may contact our Data Protection Officer 
              at dpo@pollenate.dev.
            </p>
          </section>
        </div>

        {/* Footer */}
        <div className="mt-16 pt-8 border-t border-slate-200 dark:border-neutral-800">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-slate-500 dark:text-neutral-400">
            <p>© {new Date().getFullYear()} <a href="https://bluehive.com?utm_source=pollenate&utm_medium=website&utm_campaign=footer_copyright" target="_blank" rel="noopener noreferrer" className="hover:text-slate-700 dark:hover:text-neutral-300 transition-colors">BlueHive Health</a>, LLC. All rights reserved.</p>
            <div className="flex items-center gap-6">
              <Link to="/terms" className="hover:text-slate-700 dark:text-neutral-300 transition-colors">Terms of Service</Link>
              <Link to="/" className="hover:text-slate-700 dark:text-neutral-300 transition-colors">Back to Home</Link>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
